/**
 * Shared invoice settings fields for admin pump detail, admin invoice page, and pump manager.
 */
import { resolveTaxTypeLabel, stateNameByCode } from '../../../constants/indianGstStates';
import {
  GST_STATE_OPTIONS,
  applyStateSelection,
  gstinSideEffects,
  isPanAutoFromGstin,
} from '../../../utils/invoiceSettingsForm';
import { districtSelectOptions } from '../../../constants/indianDistricts';
import PumpLocationMap from './PumpLocationMap';
import SearchableSelect from './SearchableSelect';

const InvoiceSettingsFormFields = ({ values, errors = {}, onChange, showBillNo = true }) => {
  const gstEnabled = values.invoice_gst_enabled !== false && values.invoice_gst_enabled !== '0';
  const panAutoFilled = isPanAutoFromGstin(values);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'gst_no') {
      // Atomic patch so PAN / state autofill is not lost across batched setState calls.
      onChange(gstinSideEffects(value, values));
      return;
    }
    let nextValue = type === 'checkbox' ? checked : value;
    if (name === 'pan_no') {
      nextValue = String(nextValue).toUpperCase();
    }
    onChange(name, nextValue);
  };

  const handlePumpStateChange = (code) => {
    const next = applyStateSelection(code);
    const patch = {
      pump_state_code: next.pump_state_code,
      pump_state_name: next.pump_state_name,
      pump_district: '',
    };
    if (gstEnabled) {
      patch.state_code = next.state_code;
    }
    onChange(patch);
  };

  const handlePlaceSupplyChange = (code) => {
    onChange({
      place_supply_code: code,
      place: stateNameByCode(code) || '',
    });
  };

  const usePumpAddressForInvoice = () => {
    onChange('invoice_address', values.pump_address || '');
  };

  const taxHint = resolveTaxTypeLabel(values.pump_state_code, values.place_supply_code);

  return (
    <>
      <div className="mb-3">
        <label className="form-label">Invoice type</label>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="invoice_gst_enabled"
            name="invoice_gst_enabled"
            checked={gstEnabled}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="invoice_gst_enabled">
            {gstEnabled ? 'GST tax invoice' : 'Non-GST bill / invoice'}
          </label>
        </div>
      </div>

      <h6 className="mt-4 mb-3">Pump location</h6>
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label className="form-label">State</label>
          <SearchableSelect
            inputId="invoice-pump-state"
            name="pump_state_code"
            placeholder="Search state…"
            options={GST_STATE_OPTIONS}
            value={values.pump_state_code || ''}
            onChange={handlePumpStateChange}
            noOptionsMessage="No state found"
          />
          {errors.pump_state_code && <small className="text-danger">{errors.pump_state_code}</small>}
        </div>
        <div className="col-md-4">
          <label className="form-label">District</label>
          <SearchableSelect
            inputId="invoice-pump-district"
            name="pump_district"
            placeholder={values.pump_state_code ? 'Search district…' : 'Select state first'}
            options={districtSelectOptions(values.pump_state_code, values.pump_district)}
            value={values.pump_district || ''}
            isDisabled={!values.pump_state_code}
            isCreatable
            onChange={(value) => onChange('pump_district', value)}
            noOptionsMessage="No district found — type to add"
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">Latitude</label>
          <input
            name="latitude"
            type="number"
            step="any"
            className="form-control"
            value={values.latitude ?? ''}
            onChange={handleChange}
          />
          {errors.latitude && <small className="text-danger">{errors.latitude}</small>}
        </div>
        <div className="col-md-2">
          <label className="form-label">Longitude</label>
          <input
            name="longitude"
            type="number"
            step="any"
            className="form-control"
            value={values.longitude ?? ''}
            onChange={handleChange}
          />
          {errors.longitude && <small className="text-danger">{errors.longitude}</small>}
        </div>
      </div>
      <div className="mb-3">
        <PumpLocationMap latitude={values.latitude} longitude={values.longitude} />
      </div>

      <h6 className="mt-4 mb-3">Pump contact details</h6>
      <div className="mb-3">
        <label className="form-label">Pump address</label>
        <textarea
          name="pump_address"
          className="form-control"
          rows={3}
          value={values.pump_address || ''}
          onChange={handleChange}
        />
      </div>
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <label className="form-label">Phone</label>
          <input name="pump_phone" className="form-control" value={values.pump_phone || ''} onChange={handleChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label">Email</label>
          <input name="pump_email" type="email" className="form-control" value={values.pump_email || ''} onChange={handleChange} />
        </div>
      </div>

      <h6 className="mt-4 mb-3">Invoice details</h6>
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label className="form-label mb-0">Invoice address</label>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={usePumpAddressForInvoice}>
            Use pump address
          </button>
        </div>
        <textarea name="invoice_address" className="form-control" rows={3} value={values.invoice_address || ''} onChange={handleChange} />
        {errors.invoice_address && <small className="text-danger">{errors.invoice_address}</small>}
      </div>

      <div className="row g-2">
        <div className="col-md-6 mb-3">
          <label className="form-label">Dealer</label>
          <input name="dealer" className="form-control" value={values.dealer || ''} onChange={handleChange} />
          {errors.dealer && <small className="text-danger">{errors.dealer}</small>}
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">Place of supply</label>
          <SearchableSelect
            inputId="invoice-place-supply"
            name="place_supply_code"
            placeholder="Search place of supply…"
            options={GST_STATE_OPTIONS}
            value={values.place_supply_code || ''}
            onChange={handlePlaceSupplyChange}
            noOptionsMessage="No state found"
          />
          {errors.place_supply_code && <small className="text-danger">{errors.place_supply_code}</small>}
          {values.place_supply_code && (
            <small className="text-muted d-block mt-1">Tax applicable: {taxHint}</small>
          )}
        </div>
      </div>

      {gstEnabled && (
        <div className="row g-2">
          <div className="col-md-6 mb-3">
            <label className="form-label">GST number</label>
            <input name="gst_no" className="form-control" maxLength={15} value={values.gst_no || ''} onChange={handleChange} />
            {errors.gst_no && <small className="text-danger">{errors.gst_no}</small>}
            <small className="text-muted d-block mt-1">
              State and PAN are auto-filled from the GSTIN.
            </small>
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label">State code</label>
            <input name="state_code" className="form-control" maxLength={2} value={values.state_code || ''} onChange={handleChange} readOnly />
          </div>
          <div className="col-md-3 mb-3">
            <label className="form-label">PAN</label>
            <input
              name="pan_no"
              className="form-control"
              maxLength={10}
              value={values.pan_no || ''}
              onChange={handleChange}
              readOnly={panAutoFilled}
              title={panAutoFilled ? 'Auto-filled from GSTIN (characters 3–12)' : undefined}
            />
            {panAutoFilled ? (
              <small className="text-muted d-block mt-1">Auto-filled from GSTIN</small>
            ) : null}
            {errors.pan_no && <small className="text-danger">{errors.pan_no}</small>}
          </div>
        </div>
      )}

      {showBillNo && (
        <div className="mb-3">
          <label className="form-label">Bill number prefix / current</label>
          <input name="bill_no" className="form-control" value={values.bill_no || ''} onChange={handleChange} />
        </div>
      )}
    </>
  );
};

export default InvoiceSettingsFormFields;
